import React, { useEffect, useMemo, useState } from "react";
import { addDays, addWeeks, format, startOfWeek } from "date-fns";
import { SYM, withEllipsis } from "../utils/textSymbols";
import { APPROVAL_BELL_SLOTS } from "../components/ApprovalBell";
import "./MealsPage.css";

const STORAGE_KEY = "family-dashboard-paprika-meals-v1";

function safeParseJSON(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function toDateKey(v) {
  if (!v) return "Unscheduled";
  const s = String(v).trim();
  if (!s) return "Unscheduled";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const asNum = Number(s);
  const d =
    Number.isFinite(asNum) && asNum > 0
      ? new Date(asNum)
      : new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isLikelyRecipeUid(s) {
  const t = String(s || "").trim();
  return /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/.test(t);
}

function getRecipeUid(it) {
  if (!it || typeof it !== "object") return null;
  const u =
    it.recipe_uid ||
    it.recipeUid ||
    it.recipe_id ||
    it.recipeId ||
    (typeof it.recipe === "string" && isLikelyRecipeUid(it.recipe) ? it.recipe.trim() : null);
  if (u) return String(u).trim();
  if (it.recipe && typeof it.recipe === "object" && it.recipe.uid) {
    return String(it.recipe.uid).trim();
  }
  return null;
}

function getMealTitle(it) {
  if (it?.name && String(it.name).trim()) return String(it.name).trim();
  if (typeof it?.recipe === "string" && it.recipe.trim()) return it.recipe.trim();
  return (
    it?.recipe_name ||
    it?.recipeTitle ||
    it?.title ||
    it?.text ||
    "Meal"
  );
}

export default function MealsPage({ apiBase }) {
  const [cache, setCache] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 0 })
  );
  const [recipeModal, setRecipeModal] = useState(null);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [recipeError, setRecipeError] = useState(null);

  useEffect(() => {
    const parsed = safeParseJSON(localStorage.getItem(STORAGE_KEY));
    if (!parsed?.version) return;
    setCache(parsed);
  }, []);

  const mealSchedule = useMemo(() => {
    return Array.isArray(cache?.mealSchedule) ? cache.mealSchedule : [];
  }, [cache]);

  const itemsByDate = useMemo(() => {
    const map = new Map();
    for (const it of mealSchedule) {
      const date = toDateKey(
        it?.date ||
          it?.day ||
          it?.scheduled_date ||
          it?.scheduledDate ||
          it?.start_date ||
          it?.startDate
      );
      if (date === "Unscheduled") continue;
      if (!map.has(date)) map.set(date, []);
      map.get(date).push(it);
    }
    for (const [, arr] of map) {
      arr.sort((a, b) => (a?.order_flag ?? 0) - (b?.order_flag ?? 0));
    }
    return map;
  }, [mealSchedule]);

  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i += 1) {
      const d = addDays(weekStart, i);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      days.push({
        date: d,
        dateKey: `${yyyy}-${mm}-${dd}`,
        weekday: format(d, "EEE"),
        dayLabel: format(d, "MMM d")
      });
    }
    return days;
  }, [weekStart]);

  const weekRangeLabel = useMemo(() => {
    const end = addDays(weekStart, 6);
    const y = weekStart.getFullYear();
    const y2 = end.getFullYear();
    if (y === y2) {
      return `${format(weekStart, "MMM d")} ${SYM.enDash} ${format(end, "MMM d, yyyy")}`;
    }
    return `${format(weekStart, "MMM d, yyyy")} ${SYM.enDash} ${format(end, "MMM d, yyyy")}`;
  }, [weekStart]);

  const syncFromPaprika = async () => {
    setSyncLoading(true);
    setSyncError(null);
    try {
      const res = await fetch(`${apiBase}/api/GetPaprikaMealSchedule`, {
        credentials: "include"
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Sync failed (${res.status})`);
      }
      const nextCache = {
        version: 1,
        fetchedAt: Date.now(),
        mealSchedule: Array.isArray(data.mealSchedule) ? data.mealSchedule : [],
        source: data?.source || "unknown"
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextCache));
      setCache(nextCache);
    } catch (e) {
      setSyncError(e?.message || "Sync failed.");
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => {
    syncFromPaprika();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const openRecipe = async (uid) => {
    if (!uid) return;
    setRecipeModal(null);
    setRecipeError(null);
    setRecipeLoading(true);
    try {
      const res = await fetch(
        `${apiBase}/api/GetPaprikaRecipe?uid=${encodeURIComponent(uid)}`,
        { credentials: "include" }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Load failed (${res.status})`);
      setRecipeModal(data.recipe || null);
    } catch (e) {
      setRecipeError(e?.message || "Could not load recipe.");
    } finally {
      setRecipeLoading(false);
    }
  };

  const closeRecipe = () => {
    setRecipeModal(null);
    setRecipeError(null);
    setRecipeLoading(false);
  };

  return (
    <div className="meals-page">
      <div className="meals-header">
        <div id={APPROVAL_BELL_SLOTS.meals} className="approval-bell-slot" aria-hidden="true" />
        <h2 className="meals-title">Meals</h2>
        <button
          type="button"
          className="meals-refresh-btn"
          onClick={syncFromPaprika}
          disabled={syncLoading}
          aria-busy={syncLoading}
        >
          {syncLoading ? withEllipsis("Syncing") : "Refresh"}
        </button>
      </div>

      <div className="meals-week-nav">
        <button
          type="button"
          className="meals-week-arrow"
          onClick={() => setWeekStart((w) => addWeeks(w, -1))}
          aria-label="Previous week"
        >
          {SYM.chevronLeft}
        </button>
        <div className="meals-week-label">{weekRangeLabel}</div>
        <button
          type="button"
          className="meals-week-arrow"
          onClick={() => setWeekStart((w) => addWeeks(w, 1))}
          aria-label="Next week"
        >
          {SYM.chevronRight}
        </button>
      </div>

      {syncError && <div className="meals-error">{syncError}</div>}

      {!cache && (
        <div className="meals-empty-state">
          <p className="meals-empty-hint">
            Meal schedule is stored locally. Click <b>Refresh</b> to sync from Paprika.
          </p>
        </div>
      )}

      {cache && (
        <div className="meals-schedule">
          {weekDays.map((d) => {
            const items = itemsByDate.get(d.dateKey) || [];
            return (
              <div key={d.dateKey} className="meals-day-block">
                <div className="meals-day-header">
                  <span className="meals-day-weekday">{d.weekday}</span>
                  <span className="meals-day-date">{d.dayLabel}</span>
                </div>
                <div className="meals-day-items">
                  {items.length === 0 && (
                    <div className="meals-item meals-item--empty">No meals</div>
                  )}
                  {items.map((it, idx) => {
                    const recipeUid = getRecipeUid(it);
                    const title = getMealTitle(it);
                    const interactive = !!recipeUid;
                    return (
                      <div
                        key={it?.uid || it?.id || `${d.dateKey}-${idx}`}
                        className={`meals-item ${interactive ? "meals-item--recipe" : ""}`}
                      >
                        {interactive ? (
                          <button
                            type="button"
                            className="meals-item-title-btn"
                            onClick={() => openRecipe(recipeUid)}
                          >
                            {title}
                          </button>
                        ) : (
                          <div className="meals-item-title">{title}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(recipeLoading || recipeModal || recipeError) && (
        <div className="meals-modal-overlay" onClick={closeRecipe} role="presentation">
          <div
            className="meals-modal meals-recipe-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-busy={recipeLoading}
          >
            <div className="meals-modal-header">
              <h3>{recipeModal?.name || "Recipe"}</h3>
              <button type="button" className="meals-modal-close" onClick={closeRecipe} aria-label="Close">
                {SYM.close}
              </button>
            </div>
            <div className="meals-modal-body">
              {recipeLoading && <div className="meals-modal-loading">{withEllipsis("Loading")}</div>}
              {recipeError && <div className="meals-modal-error">{recipeError}</div>}
              {!recipeLoading && recipeModal && (
                <>
                  {(recipeModal.prep_time || recipeModal.cook_time || recipeModal.servings) && (
                    <div className="meals-recipe-meta">
                      {recipeModal.prep_time && (
                        <span>Prep: {recipeModal.prep_time}</span>
                      )}
                      {recipeModal.cook_time && (
                        <span>Cook: {recipeModal.cook_time}</span>
                      )}
                      {recipeModal.servings && (
                        <span>Serves: {recipeModal.servings}</span>
                      )}
                    </div>
                  )}
                  {recipeModal.source_url && (
                    <p className="meals-recipe-source">
                      <a href={recipeModal.source_url} target="_blank" rel="noopener noreferrer">
                        Source
                      </a>
                    </p>
                  )}
                  {recipeModal.ingredients && (
                    <section className="meals-recipe-section">
                      <h4>Ingredients</h4>
                      <pre className="meals-recipe-pre">{recipeModal.ingredients}</pre>
                    </section>
                  )}
                  {recipeModal.directions && (
                    <section className="meals-recipe-section">
                      <h4>Directions</h4>
                      <pre className="meals-recipe-pre">{recipeModal.directions}</pre>
                    </section>
                  )}
                  {recipeModal.notes && (
                    <section className="meals-recipe-section">
                      <h4>Notes</h4>
                      <pre className="meals-recipe-pre">{recipeModal.notes}</pre>
                    </section>
                  )}
                  {recipeModal.nutritional_info && (
                    <section className="meals-recipe-section">
                      <h4>Nutrition</h4>
                      <pre className="meals-recipe-pre">{recipeModal.nutritional_info}</pre>
                    </section>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
